import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FadeIn, RevealText, SlideReveal } from '../components/AnimatedSection';
import { FloatingHexagons, HexShape } from '../components/HexagonLogo';
import { MarqueeText } from '../components/MarqueeScroll';
import { ParallaxImage, WordReveal, MagneticButton, HoverImage } from '../components/ScrollEffects';
import { HiMail, HiPhone, HiLocationMarker, HiArrowRight } from 'react-icons/hi';
import { FaInstagram, FaLinkedinIn, FaWhatsapp } from 'react-icons/fa';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    window.location.href = `mailto:hello@htvdigital.in?subject=Enquiry from ${form.name}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nService: ${form.service}\n\n${form.message}`)}`;
  };

  return (
    <div className="page-wrapper">
      {/* Let's Talk Hero with image */}
      <section style={styles.heroSection}>
        <div style={styles.heroBg}>
          <motion.img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80"
            alt="Modern office space"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          />
          <div style={styles.heroOverlay} />
        </div>
        <FloatingHexagons />
        <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <FadeIn>
            <div className="section-num" style={{ justifyContent: 'center' }}>01 &mdash; CONTACT</div>
          </FadeIn>
          <RevealText>
            <h1 style={styles.ctaTitle}>
              Let's <span style={{ fontStyle: 'italic', color: '#549E84' }}>talk</span> now<br />
              for your next<br />
              <span style={{ color: '#3E7562' }}>digital journey</span>
            </h1>
          </RevealText>

          <div style={{ position: 'absolute', top: '20%', left: '5%', opacity: 0.5 }}>
            <motion.div animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }} transition={{ duration: 6, repeat: Infinity }}>
              <HexShape size={60} color="#3E7562" opacity={0.08} />
            </motion.div>
          </div>
          <div style={{ position: 'absolute', top: '30%', right: '8%', opacity: 0.5 }}>
            <motion.div animate={{ y: [0, 10, 0], rotate: [0, -5, 0] }} transition={{ duration: 7, repeat: Infinity }}>
              <HexShape size={80} color="#3E7562" opacity={0.06} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section style={{ borderTop: '1px solid rgba(62,117,98,0.08)', borderBottom: '1px solid rgba(62,117,98,0.08)' }}>
        <MarqueeText texts={["Let's Talk", 'Get In Touch', 'Start Your Journey', 'Book a Call']} speed={25} reverse />
      </section>

      {/* Photo scroll gallery */}
      <section className="section" style={{ background: '#0B0B0B', paddingBottom: 40 }}>
        <div className="container">
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <h2 className="section-title" style={{ fontSize: '1.5rem' }}>
                We bring <span className="gradient-text">stories to life.</span>
              </h2>
            </div>
          </FadeIn>
          <div className="grid-3" style={{ gap: 16 }}>
            {galleryImages.map((img, i) => (
              <FadeIn key={i} delay={i * 0.12}>
                <HoverImage
                  src={img.src}
                  alt={img.label}
                  overlay={img.label}
                  style={{ width: '100%', height: 240 }}
                />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="section" style={{ background: '#111112' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <WordReveal
              text="Ready to transform your digital presence?"
              className="section-title"
              style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: 800, letterSpacing: '-1px', lineHeight: 1.2, display: 'block' }}
            />
          </div>

          <div className="grid-contact">
            <SlideReveal direction="left">
              <motion.form onSubmit={handleSubmit} style={styles.form} whileHover={{ borderColor: 'rgba(62,117,98,0.15)' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 32, letterSpacing: '-0.3px' }}>Send us a message</h3>
                <div className="form-row">
                  <div style={styles.group}>
                    <label style={styles.label}>Full Name</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your name" required style={styles.input} />
                  </div>
                  <div style={styles.group}>
                    <label style={styles.label}>Email</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@company.com" required style={styles.input} />
                  </div>
                </div>
                <div className="form-row">
                  <div style={styles.group}>
                    <label style={styles.label}>Company</label>
                    <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Company name" style={styles.input} />
                  </div>
                  <div style={styles.group}>
                    <label style={styles.label}>Service</label>
                    <select name="service" value={form.service} onChange={handleChange} style={styles.input}>
                      <option value="">Select a service</option>
                      <option value="AI Video">AI Video Production</option>
                      <option value="Research">Market Research</option>
                      <option value="Content">AI Content Creation</option>
                      <option value="Web">Web Design & Dev</option>
                      <option value="All">All Services</option>
                    </select>
                  </div>
                </div>
                <div style={styles.group}>
                  <label style={styles.label}>Message</label>
                  <textarea name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your goals..." rows={5} style={{ ...styles.input, resize: 'vertical' }} />
                </div>
                <MagneticButton style={{ width: '100%' }}>
                  <motion.button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '16px' }} whileTap={{ scale: 0.98 }}>
                    Send Message <HiArrowRight />
                  </motion.button>
                </MagneticButton>
              </motion.form>
            </SlideReveal>

            <SlideReveal direction="right" delay={0.15}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={styles.infoCard}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 12 }}>Get in touch</h3>
                  <p style={{ color: '#777', fontSize: '0.85rem', lineHeight: 1.8, marginBottom: 28 }}>
                    Start with a free, no-obligation discovery call.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {[
                      { icon: <HiMail size={18} />, label: 'Email', value: 'hello@htvdigital.in', href: 'mailto:hello@htvdigital.in' },
                      { icon: <HiPhone size={18} />, label: 'Phone', value: '+91 99999 00000', href: 'tel:+919999900000' },
                      { icon: <HiLocationMarker size={18} />, label: 'Location', value: 'Hyderabad, Telangana' },
                    ].map((c, i) => (
                      <motion.a
                        key={i}
                        href={c.href || '#'}
                        style={styles.contactRow}
                        whileHover={{ x: 4, borderColor: 'rgba(62,117,98,0.15)' }}
                      >
                        <div style={styles.contactIcon}>{c.icon}</div>
                        <div>
                          <span style={{ display: 'block', fontSize: '0.65rem', color: '#777', textTransform: 'uppercase', letterSpacing: 1 }}>{c.label}</span>
                          <span style={{ fontSize: '0.9rem', color: '#fff', fontWeight: 500 }}>{c.value}</span>
                        </div>
                      </motion.a>
                    ))}
                  </div>
                </div>
                <div style={styles.socialCard}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 16 }}>Follow us</h4>
                  <div style={{ display: 'flex', gap: 12 }}>
                    {[
                      { icon: <FaInstagram size={18} />, label: 'Instagram' },
                      { icon: <FaLinkedinIn size={18} />, label: 'LinkedIn' },
                      { icon: <FaWhatsapp size={18} />, label: 'WhatsApp' },
                    ].map((s, i) => (
                      <MagneticButton key={i} style={{ flex: 1 }}>
                        <div style={styles.socialBtn}>
                          {s.icon}
                          <span style={{ fontSize: '0.7rem' }}>{s.label}</span>
                        </div>
                      </MagneticButton>
                    ))}
                  </div>
                </div>

                {/* Office image */}
                <HoverImage
                  src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80"
                  alt="Our office"
                  overlay="Hyderabad, Telangana"
                  style={{ width: '100%', height: 200 }}
                />
              </div>
            </SlideReveal>
          </div>
        </div>
      </section>
    </div>
  );
}

const galleryImages = [
  { src: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&q=80', label: 'Video Production' },
  { src: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80', label: 'Strategy Sessions' },
  { src: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600&q=80', label: 'Brand Design' },
];

const styles = {
  heroSection: {
    minHeight: '70vh',
    display: 'flex',
    alignItems: 'center',
    paddingTop: 180,
    paddingBottom: 80,
    position: 'relative',
    overflow: 'hidden',
  },
  heroBg: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(11,11,11,0.85) 0%, rgba(11,11,11,0.65) 50%, rgba(11,11,11,0.92) 100%)',
  },
  ctaTitle: {
    fontSize: 'clamp(3rem, 7vw, 5.5rem)',
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: '-3px',
    color: '#fff',
  },
  form: { padding: 40, background: '#161618', border: '1px solid rgba(255,255,255,0.04)', transition: '0.4s' },
  group: { marginBottom: 20 },
  label: { display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#777', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1.5 },
  input: { width: '100%', padding: '14px 16px', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(11,11,11,0.6)', color: '#fff', fontSize: '0.9rem', fontFamily: "'Montserrat', sans-serif", outline: 'none', transition: '0.3s' },
  infoCard: { padding: 36, background: '#161618', border: '1px solid rgba(255,255,255,0.04)' },
  contactRow: { display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: 'rgba(62,117,98,0.03)', border: '1px solid rgba(255,255,255,0.03)', transition: '0.3s', textDecoration: 'none' },
  contactIcon: { width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(62,117,98,0.1)', color: '#3E7562', flexShrink: 0 },
  socialCard: { padding: 28, background: '#161618', border: '1px solid rgba(255,255,255,0.04)' },
  socialBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '16px 12px', border: '1px solid rgba(255,255,255,0.04)', color: '#777', transition: '0.3s', cursor: 'pointer', width: '100%' },
};
